import { useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { api, authStorage } from "./api";
import { AuthContext, type AuthContextValue, type User } from "./auth-context";

type LoginPayload = { access_token: string; token_type: string; user: User };

function storedUser(): User | null {
  try {
    return JSON.parse(
      localStorage.getItem(authStorage.userKey) || "null",
    ) as User | null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(storedUser);
  const [loading, setLoading] = useState(Boolean(authStorage.token()));

  useEffect(() => {
    if (!authStorage.token()) return;
    api<User>("/api/auth/me")
      .then((nextUser) => {
        setUser(nextUser);
        localStorage.setItem(authStorage.userKey, JSON.stringify(nextUser));
      })
      .catch(() => {
        authStorage.clear();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login: async (username, password) => {
        const result = await api<LoginPayload>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ username, password }),
        });
        localStorage.setItem(authStorage.tokenKey, result.access_token);
        localStorage.setItem(authStorage.userKey, JSON.stringify(result.user));
        setUser(result.user);
      },
      logout: () => {
        authStorage.clear();
        setUser(null);
      },
      updateUser: (nextUser) => {
        setUser(nextUser);
        localStorage.setItem(authStorage.userKey, JSON.stringify(nextUser));
      },
      can: (module) =>
        user?.role === "admin" || Boolean(user?.modules?.includes(module)),
    }),
    [loading, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
